import { useState, useCallback, useEffect } from 'react';

const PROPERTY_API_URL = 'https://property-lookup-api.anassk.workers.dev';
const PROPERTY_API_KEY = '19285196e984c67c9c96784becbd3464e692815a4393e4c948be460a2019182e';

// Get user/team IDs from localStorage
function getUserId() {
  return localStorage.getItem('leadgen_client_id') || `user_${Date.now()}`;
}

function getTeamId() {
  return localStorage.getItem('leadgen_team_id') || 'default';
}

export function useLeadsDatabase() {
  const [dbLeads, setDbLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  const userId = getUserId();
  const teamId = getTeamId();

  // Fetch leads from D1
  const fetchLeads = useCallback(async (filters = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({ teamId, ...filters });
      const response = await fetch(`${PROPERTY_API_URL}/leads?${params}`, {
        headers: { 'X-API-Key': PROPERTY_API_KEY }
      });

      const data = await response.json();
      if (data.success) {
        setDbLeads(data.leads || []);
        return data.leads;
      } else {
        throw new Error(data.error || 'Failed to fetch leads');
      }
    } catch (e) {
      setError(e.message);
      console.error('Failed to fetch leads:', e);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [teamId]);

  // Add leads to D1 database
  const addLeads = useCallback(async (leads) => {
    if (!leads?.length) return { success: false, error: 'No leads to add' };

    setIsSyncing(true);
    setError(null);

    try {
      const response = await fetch(`${PROPERTY_API_URL}/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': PROPERTY_API_KEY
        },
        body: JSON.stringify({
          leads: leads.map(lead => ({
            // Basic info
            streetAddress: lead.streetAddress,
            city: lead.city,
            state: lead.state,
            zip: lead.zip,
            lat: lead.lat,
            lng: lead.lng,
            ownerName: lead.ownerName,
            assessedValue: lead.assessedValue,
            sqft: lead.sqft,
            yearBuilt: lead.yearBuilt,
            ownerOccupied: lead.ownerOccupied,
            score: lead.score || 0,
            // Enrichment status
            enriched: lead.enriched || false,
            // Property details
            bedrooms: lead.bedrooms,
            bathrooms: lead.bathrooms,
            lotSize: lead.lotSize,
            // Roof data (measured or estimated)
            roofSqft: lead.roofSqft || lead.estRoofSqft,
            roofAge: lead.roofAge,
            roofType: lead.roofType,
            needsRoofReplacement: lead.needsRoofReplacement,
            // Roof costs
            roofCostAsphalt: lead.roofCostAsphalt || lead.estRoofCostLow,
            roofCostMetal: lead.roofCostMetal || lead.estRoofCostHigh,
            // Solar/satellite measurements
            roofSqftMeasured: lead.roofSqftMeasured || false,
            roofPitch: lead.roofPitch,
            roofSegments: lead.roofSegments
          })),
          userId,
          teamId
        })
      });

      const data = await response.json();
      if (data.success) {
        // Refresh leads list
        await fetchLeads();
      }
      return data;
    } catch (e) {
      setError(e.message);
      console.error('Failed to add leads:', e);
      return { success: false, error: e.message };
    } finally {
      setIsSyncing(false);
    }
  }, [userId, teamId, fetchLeads]);

  // Update a lead
  const updateLead = useCallback(async (leadId, updates) => {
    setError(null);

    try {
      const response = await fetch(`${PROPERTY_API_URL}/leads/${leadId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': PROPERTY_API_KEY
        },
        body: JSON.stringify(updates)
      });

      const data = await response.json();
      if (data.success) {
        // Update local state
        setDbLeads(prev => prev.map(l =>
          l.id === leadId ? { ...l, ...updates } : l
        ));
      }
      return data;
    } catch (e) {
      setError(e.message);
      return { success: false, error: e.message };
    }
  }, []);

  // Delete a lead
  const deleteLead = useCallback(async (leadId) => {
    try {
      const response = await fetch(`${PROPERTY_API_URL}/leads/${leadId}`, {
        method: 'DELETE',
        headers: { 'X-API-Key': PROPERTY_API_KEY }
      });

      const data = await response.json();
      if (data.success) {
        setDbLeads(prev => prev.filter(l => l.id !== leadId));
      }
      return data;
    } catch (e) {
      return { success: false, error: e.message };
    }
  }, []);

  // Enrich selected leads with RentCast data
  const enrichLeads = useCallback(async (leadIds, onProgress) => {
    if (!leadIds?.length) return { success: false, error: 'No leads to enrich' };

    setIsSyncing(true);
    setError(null);

    try {
      const response = await fetch(`${PROPERTY_API_URL}/leads/enrich`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': PROPERTY_API_KEY
        },
        body: JSON.stringify({ leadIds, teamId })
      });

      const data = await response.json();
      if (data.success) {
        // Refresh leads to get updated data
        await fetchLeads();
      }
      return data;
    } catch (e) {
      setError(e.message);
      return { success: false, error: e.message };
    } finally {
      setIsSyncing(false);
    }
  }, [teamId, fetchLeads]);

  // Get team stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(
        `${PROPERTY_API_URL}/leads/stats?teamId=${teamId}`,
        { headers: { 'X-API-Key': PROPERTY_API_KEY } }
      );

      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
      return data.stats;
    } catch (e) {
      console.error('Failed to fetch stats:', e);
      return null;
    }
  }, [teamId]);

  // Qualify leads (mark as qualified based on criteria)
  const qualifyLeads = useCallback(async (leadIds, qualifiedBy = userId) => {
    const results = [];
    for (const id of leadIds) {
      const result = await updateLead(id, {
        qualified: 1,
        status: 'qualified'
      });
      results.push({ id, ...result });
    }
    return results;
  }, [updateLead, userId]);

  // Mark leads as flyer generated
  const markFlyersGenerated = useCallback(async (leadIds) => {
    const results = [];
    for (const id of leadIds) {
      const result = await updateLead(id, {
        flyer_generated: 1
      });
      results.push({ id, ...result });
    }
    return results;
  }, [updateLead]);

  // Convert DB lead format to app format
  const convertDbLeadToAppLead = useCallback((dbLead) => ({
    id: dbLead.id,
    streetAddress: dbLead.street_address,
    city: dbLead.city,
    state: dbLead.state,
    zip: dbLead.zip_code,
    lat: dbLead.latitude,
    lng: dbLead.longitude,
    ownerName: dbLead.owner_name,
    assessedValue: dbLead.assessed_value,
    sqft: dbLead.sqft,
    yearBuilt: dbLead.year_built,
    ownerOccupied: dbLead.owner_occupied === 1,
    enriched: dbLead.enriched === 1,
    bedrooms: dbLead.bedrooms,
    bathrooms: dbLead.bathrooms,
    lotSize: dbLead.lot_size,
    // Roof data
    roofSqft: dbLead.roof_sqft,
    roofAge: dbLead.roof_age,
    roofType: dbLead.roof_type,
    needsRoofReplacement: dbLead.roof_needs_replacement === 1,
    roofCostLow: dbLead.roof_cost_low,
    roofCostHigh: dbLead.roof_cost_high,
    roofCostAsphalt: dbLead.roof_cost_low,  // Map to frontend field names
    roofCostMetal: dbLead.roof_cost_high,
    // Satellite measurement data
    roofSqftMeasured: dbLead.roof_sqft_measured === 1,
    roofPitch: dbLead.roof_pitch_degrees,
    roofSegments: dbLead.roof_segment_count,
    // Lead status
    status: dbLead.status,
    score: dbLead.score,
    priority: dbLead.priority,
    qualified: dbLead.qualified === 1,
    flyerGenerated: dbLead.flyer_generated === 1,
    notes: dbLead.notes,
    tags: dbLead.tags ? JSON.parse(dbLead.tags) : [],
    createdAt: dbLead.created_at,
    updatedAt: dbLead.updated_at
  }), []);

  // Get leads in app format
  const getAppLeads = useCallback(() => {
    return dbLeads.map(convertDbLeadToAppLead);
  }, [dbLeads, convertDbLeadToAppLead]);

  // Check which addresses already exist in the database
  const checkExistingAddresses = useCallback(async (addresses) => {
    if (!addresses?.length) return new Set();

    try {
      // Create a set of normalized addresses to check
      const normalizedAddresses = addresses.map(a =>
        `${a.streetAddress?.toLowerCase().trim()},${a.city?.toLowerCase().trim()},${a.state?.toLowerCase().trim()}`
      );

      // Fetch existing leads for this team
      const response = await fetch(`${PROPERTY_API_URL}/leads?teamId=${teamId}`, {
        headers: { 'X-API-Key': PROPERTY_API_KEY }
      });

      const data = await response.json();
      if (!data.success || !data.leads) return new Set();

      // Create a set of existing addresses
      const existingSet = new Set(
        data.leads.map(l =>
          `${l.street_address?.toLowerCase().trim()},${l.city?.toLowerCase().trim()},${l.state?.toLowerCase().trim()}`
        )
      );

      // Return set of indices that exist in DB
      const existingIndices = new Set();
      normalizedAddresses.forEach((addr, index) => {
        if (existingSet.has(addr)) {
          existingIndices.add(index);
        }
      });

      return existingIndices;
    } catch (e) {
      console.error('Failed to check existing addresses:', e);
      return new Set();
    }
  }, [teamId]);

  // Get address key for comparison
  const getAddressKey = useCallback((lead) => {
    return `${lead.streetAddress?.toLowerCase().trim()},${lead.city?.toLowerCase().trim()},${lead.state?.toLowerCase().trim()}`;
  }, []);

  return {
    // State
    dbLeads,
    leads: getAppLeads(),
    isLoading,
    isSyncing,
    error,
    stats,

    // Actions
    fetchLeads,
    addLeads,
    updateLead,
    deleteLead,
    enrichLeads,
    fetchStats,
    qualifyLeads,
    markFlyersGenerated,

    // Helpers
    convertDbLeadToAppLead,
    checkExistingAddresses,
    getAddressKey,
    userId,
    teamId
  };
}

export default useLeadsDatabase;
